import React, { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { FiCreditCard, FiBriefcase, FiDollarSign, FiFileText, FiCalendar, FiTrash2 } from 'react-icons/fi';
import { HiOutlineCheck, HiOutlineArrowSmRight } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { settingsApi } from '../../../../services/api/settings.api';
import { toastService } from '../../../../services/toast';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const FinancialSettings = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  // الإعدادات الحالية المحفوظة في الداتابيز
  const [savedSettings, setSavedSettings] = useState({
    enableBankTransfer: false,
    enableOnlinePayment: false,
    enableInstallment: false,
    enableLocalInstallment: false,
    enablePaymentTerms: false,
    bankAccounts: [],
    booksTerms: '',
    coursesTerms: '',
    generalTerms: ''
  });
  
  // الإعدادات المؤقتة اللي بنعدل فيها
  const [tempSettings, setTempSettings] = useState({
    enableBankTransfer: false,
    enableOnlinePayment: false,
    enableInstallment: false,
    enableLocalInstallment: false,
    enablePaymentTerms: false,
    bankAccounts: [],
    booksTerms: '',
    coursesTerms: '',
    generalTerms: ''
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const token = sessionStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }
        
        // نجيب الإعدادات العامة والبنوك
        const [settingsResponse, banksResponse] = await Promise.all([
          settingsApi.getPaymentSettings(),
          settingsApi.getBankAccounts()
        ]);
        
        // ندمج الإعدادات مع البنوك
        const settings = {
          ...settingsResponse,
          bankAccounts: banksResponse || []
        };
        
        setSavedSettings(settings);
        setTempSettings(settings);
        
      } catch (error) {
        if (error.response?.status === 403) {
          toastService.error('غير مصرح لك بالوصول إلى هذه الصفحة');
          // تم إزالة التحويل لمنع طرد المستخدم من الصفحة
        } else {
          toastService.error('حدث خطأ أثناء تحميل إعدادات الدفع');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [navigate]);

  // تحديث الإعدادات المؤقتة
  const handleChange = (key, value) => {
    setTempSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // تحديث الإعدادات المؤقتة
  const handleInputChange = (e, accountIndex = 0) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('bank.')) {
      const bankField = name.split('.')[1];
      const updatedAccounts = tempSettings.bankAccounts.map((account, idx) => {
        if (idx === accountIndex) {
          return { ...account, [bankField]: value };
        }
        return account;
      });
      
      setTempSettings(prev => ({
        ...prev,
        bankAccounts: updatedAccounts
      }));
    } else {
      setTempSettings(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // حذف حساب بنكي
  const handleDeleteBankAccount = async (accountId) => {
    try {
      setLoading(true);
      
      // نحذف الحساب
      await settingsApi.deleteBankAccount(accountId);
      
      // نحدث القائمة
      const updatedAccounts = tempSettings.bankAccounts.filter(bank => bank.id !== accountId);
      
      setTempSettings(prev => ({
        ...prev,
        bankAccounts: updatedAccounts
      }));
      setSavedSettings(prev => ({
        ...prev,
        bankAccounts: updatedAccounts
      }));
      
      toastService.success('تم حذف الحساب البنكي بنجاح');
    } catch (error) {
      toastService.error('حدث خطأ أثناء حذف الحساب البنكي');
    } finally {
      setLoading(false);
    }
  };

  // حفظ الإعدادات العامة في الداتابيز
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // نحفظ الإعدادات العامة
      const savedSettings = await settingsApi.updatePaymentSettings({
        enableBankTransfer: tempSettings.enableBankTransfer,
        enableOnlinePayment: tempSettings.enableOnlinePayment,
        enableInstallment: tempSettings.enableInstallment,
        enableLocalInstallment: tempSettings.enableLocalInstallment,
        enablePaymentTerms: tempSettings.enablePaymentTerms,
        booksTerms: tempSettings.booksTerms,
        coursesTerms: tempSettings.coursesTerms,
        generalTerms: tempSettings.generalTerms,
        installmentTerms: tempSettings.installmentTerms
      });

      // نحفظ البنوك الجديدة
      if (Array.isArray(tempSettings.bankAccounts)) {
        for (const account of tempSettings.bankAccounts) {
          if (!account.id) {
            // حساب جديد
            try {
              await settingsApi.saveBankAccount(account);
            } catch (error) {
              if (error.response?.data?.errors) {
                const errorMessages = Object.values(error.response.data.errors)
                  .flat()
                  .map(msg => msg.replace('bank name', 'اسم البنك')
                                 .replace('account number', 'رقم الحساب')
                                 .replace('account holder', 'اسم صاحب الحساب')
                                 .replace('iban', 'الآيبان'));
                toastService.error(errorMessages.join('\n'));
              }
            }
          }
        }
      }

      toastService.success('تم حفظ الإعدادات بنجاح');

    } catch (error) {
      toastService.error('حدث خطأ أثناء حفظ الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  // حفظ الحساب البنكي في الداتابيز
  const handleSaveBankAccount = async (accountData) => {
    try {
      setLoading(true);
      const savedAccount = await settingsApi.saveBankAccount(accountData);

      // نحدث القائمة
      const updatedAccounts = [...tempSettings.bankAccounts];
      if (accountData.id) {
        // تحديث حساب موجود
        const index = updatedAccounts.findIndex(acc => acc.id === accountData.id);
        if (index !== -1) {
          updatedAccounts[index] = savedAccount;
        }
      } else {
        // إضافة حساب جديد
        updatedAccounts.push(savedAccount);
      }

      setTempSettings(prev => ({ ...prev, bankAccounts: updatedAccounts }));
      setSavedSettings(prev => ({ ...prev, bankAccounts: updatedAccounts }));

      toastService.success('تم حفظ الحساب البنكي بنجاح');
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors)
          .flat()
          .map(msg => msg.replace('bank name', 'اسم البنك')
            .replace('account number', 'رقم الحساب')
            .replace('account holder', 'اسم صاحب الحساب')
            .replace('iban', 'الآيبان')
            .replace('field is required', 'مطلوب'));
        errorMessages.forEach(message => toastService.error(message));
      } else {
        toastService.error(error.response?.data?.message || 'حدث خطأ أثناء حفظ الحساب البنكي');
      }
    } finally {
      setLoading(false);
    }
  };

  const addBankAccount = () => {
    const newAccount = {
      iban: '',
      account_number: '',
      account_holder: '',
      bank_name: ''
    };
    setTempSettings(prev => ({
      ...prev,
      bankAccounts: [...prev.bankAccounts, newAccount]
    }));
  };

  const removeBankAccount = (index) => {
    setTempSettings(prev => ({
      ...prev,
      bankAccounts: (prev.bankAccounts || []).filter((_, idx) => idx !== index)
    }));
  };

  const tabs = [
    {
      name: 'طرق الدفع',
      icon: FiCreditCard,
      content: (
        <div className="p-6 space-y-8">
          <div className="grid gap-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-4 space-x-reverse">
                  <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                    <FiBriefcase className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">التحويل البنكي</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">تمكين الدفع عن طريق التحويل البنكي</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempSettings.enableBankTransfer}
                    onChange={(e) => handleChange('enableBankTransfer', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-emerald-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-4 space-x-reverse">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <FiCreditCard className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">الدفع اونلاين</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">تمكين الدفع عن طريق البطاقات الائتمانية</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempSettings.enableOnlinePayment}
                    onChange={(e) => handleChange('enableOnlinePayment', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-4 space-x-reverse">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <FiCalendar className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">تقسيط شركات الدفع</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">تمكين الدفع بالتقسيط عن طريق تمارا</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempSettings.enableInstallment}
                    onChange={(e) => handleChange('enableInstallment', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-purple-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-4 space-x-reverse">
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                    <FiDollarSign className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">تقسيط محلي</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">تمكين التقسيط المحلي عبر المنصة (تاب)</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempSettings.enableLocalInstallment}
                    onChange={(e) => handleChange('enableLocalInstallment', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-indigo-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-4 space-x-reverse">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <FiFileText className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">شروط الدفع</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">تفعيل وتعديل شروط الدفع</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tempSettings.enablePaymentTerms}
                    onChange={(e) => handleChange('enablePaymentTerms', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:bg-amber-600 after:content-[''] after:absolute after:top-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 dose-toggle-fix dose-toggle-clean"></div>
                </label>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
              <div className="space-y-4">
                <div className="flex items-start space-x-3 space-x-reverse">
                  <div className="flex-shrink-0 w-5 h-5 text-blue-500">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    يمكنك تفعيل أو تعطيل طرق الدفع في أي وقت. عند تعطيل طريقة دفع، لن تظهر للعملاء في صفحة الدفع.
                  </p>
                </div>
                <div className="flex items-start space-x-3 space-x-reverse">
                  <div className="flex-shrink-0 w-5 h-5 text-yellow-500">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"></path>
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    تأكد من إعداد وتكوين طريقة الدفع بشكل صحيح قبل تفعيلها للعملاء.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      name: 'التحويل البنكي',
      icon: FiBriefcase,
      disabled: !tempSettings.enableBankTransfer,
      content: (
        <div className="p-6">
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">تفاصيل الحسابات البنكية</h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={addBankAccount}
                    className="inline-flex items-center p-2 border border-transparent rounded-full text-emerald-600 bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 dark:text-emerald-400"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 01-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="inline-flex items-center p-2 border border-transparent rounded-full text-blue-600 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 dark:text-blue-400"
                  >
                    {loading ? (
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              
              {(tempSettings.bankAccounts || []).map((bank, index) => (
                <div key={bank.id || index} className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      حساب بنكي {index + 1}
                    </h3>
                    <FiTrash2 
                      onClick={() => handleDeleteBankAccount(bank.id)}
                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 cursor-pointer"
                      title="حذف الحساب البنكي"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-6">
                    {/* نموذج تعديل البيانات */}
                    <div className="space-y-4 pt-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          الآيبان
                        </label>
                        <input
                          type="text"
                          name="bank.iban"
                          value={bank.iban}
                          onChange={(e) => handleInputChange(e, index)}
                          className="input-base"
                          dir="ltr"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          رقم الحساب
                        </label>
                        <input
                          type="text"
                          name="bank.account_number"
                          value={bank.account_number}
                          onChange={(e) => handleInputChange(e, index)}
                          className="input-base"
                          dir="ltr"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          اسم صاحب الحساب
                        </label>
                        <input
                          type="text"
                          name="bank.account_holder"
                          value={bank.account_holder}
                          onChange={(e) => handleInputChange(e, index)}
                          className="input-base"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          اسم البنك
                        </label>
                        <input
                          type="text"
                          name="bank.bank_name"
                          value={bank.bank_name}
                          onChange={(e) => handleInputChange(e, index)}
                          className="input-base"
                        />
                      </div>
                    </div>

                    {/* البطاقة المصممة */}
                    <div className="flex items-center min-h-[400px]">
                      <div className="relative bg-gradient-to-br from-slate-600 to-slate-800 dark:from-gray-800 dark:to-gray-900 rounded-xl p-6 overflow-hidden shadow-lg w-full">
                        {/* Pattern Background */}
                        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
                        
                        {/* Feather Pattern */}
                        <div className="absolute top-0 right-0 w-48 h-48 opacity-10">
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-full h-full fill-current">
                            <path d="M92.5,7.5C84.4,15.6,76.4,23.6,68.3,31.7c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6 c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9 c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6 c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9c-0.2,0.2-0.4,0.4-0.6,0.6c-2.3,2.3-4.6,4.6-6.9,6.9C7.5,92.5,7.5,7.5,92.5,7.5z"/>
                          </svg>
                        </div>

                        {/* Shine Effect */}
                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-white opacity-10 rotate-12 transform scale-y-100 rounded-full blur-xl"></div>
                        
                        <div className="relative space-y-4 text-white">
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-sm opacity-80">اسم صاحب الحساب</p>
                              <p className="text-base font-semibold mt-1">{bank.account_holder || 'اسم صاحب الحساب'}</p>
                            </div>
                            <FiBriefcase className="w-6 h-6 opacity-80" />
                          </div>
                          
                          <div>
                            <p className="text-sm opacity-80">الآيبان</p>
                            <p className="text-base font-mono mt-1 tracking-wider" dir="ltr">{bank.iban || 'SA00 0000 0000 0000 0000 0000'}</p>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm opacity-80">رقم الحساب</p>
                              <p className="text-base font-mono mt-1 tracking-wider" dir="ltr">{bank.account_number || '000000000'}</p>
                            </div>
                            <div>
                              <p className="text-sm opacity-80">اسم البنك</p>
                              <p className="text-base font-semibold mt-1">{bank.bank_name || 'اسم البنك'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    },
    {
      name: 'الدفع اونلاين',
      icon: FiCreditCard,
      disabled: !tempSettings.enableOnlinePayment,
      content: (
        <div className="p-6">
          <div className="space-y-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">إعدادات بوابة الدفع</h3>
                <div className="grid gap-6">
                  {/* اختيار بوابة الدفع */}
                  <div className="relative flex items-center p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg ml-3">
                      <FiCreditCard className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-base font-medium text-gray-900 dark:text-white">تاب (TAP)</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">بوابة الدفع الافتراضية</p>
                    </div>
                  </div>

                  {/* مفاتيح API */}
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">مفاتيح API</h4>
                    <div className="grid gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          المفتاح العام (Public Key)
                        </label>
                        <input
                          type="text"
                          className="input-base"
                          placeholder="pk_test_..."
                          dir="ltr"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          المفتاح السري (Secret Key)
                        </label>
                        <input
                          type="password"
                          className="input-base"
                          placeholder="sk_test_..."
                          dir="ltr"
                        />
                      </div>
                    </div>
                  </div>

                  {/* إعدادات إضافية */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">إعدادات إضافية</h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="mr-3 text-sm text-gray-700 dark:text-gray-300">
                          تفعيل وضع التجربة (Sandbox Mode)
                        </span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="mr-3 text-sm text-gray-700 dark:text-gray-300">
                          تفعيل المصادقة ثنائية العوامل (3D Secure)
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      name: 'تقسيط شركات الدفع',
      icon: FiCalendar,
      disabled: !tempSettings.enableInstallment,
      content: (
        <div className="p-6">
          <div className="text-center text-gray-500 dark:text-gray-400">
            قريباً...
          </div>
        </div>
      )
    },
    {
      name: 'شروط الدفع',
      icon: FiFileText,
      disabled: !tempSettings.enablePaymentTerms,
      content: (
        <div className="p-6">
          <div className="space-y-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">شروط الدفع</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* شروط الكتب */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      شروط الكتب
                    </label>
                    <textarea
                      rows={4}
                      value={tempSettings.booksTerms || ''}
                      onChange={(e) => handleChange('booksTerms', e.target.value)}
                      className="input-base"
                      placeholder="أدخل شروط شراء الكتب..."
                      dir="rtl"
                    />
                  </div>

                  {/* شروط الدورات */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      شروط الدورات
                    </label>
                    <textarea
                      rows={4}
                      value={tempSettings.coursesTerms || ''}
                      onChange={(e) => handleChange('coursesTerms', e.target.value)}
                      className="input-base"
                      placeholder="أدخل شروط الدورات التدريبية..."
                      dir="rtl"
                    />
                  </div>

                  {/* الشروط العامة - تحت */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الشروط العامة
                    </label>
                    <textarea
                      rows={4}
                      value={tempSettings.generalTerms || ''}
                      onChange={(e) => handleChange('generalTerms', e.target.value)}
                      className="input-base"
                      placeholder="أدخل الشروط العامة للدفع..."
                      dir="rtl"
                    />
                  </div>

                  {/* شروط التقسيط المحلي */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      شروط التقسيط المحلي
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      هذه الشروط ستظهر للعميل عند تأكيد طلب التقسيط (مثل: التأخير عن السداد يؤدي لإغلاق الدورة)
                    </p>
                    <textarea
                      rows={4}
                      value={tempSettings.installmentTerms || ''}
                      onChange={(e) => handleChange('installmentTerms', e.target.value)}
                      className="input-base"
                      placeholder="أدخل شروط التقسيط..."
                      dir="rtl"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="flex-shrink-0 w-5 h-5 text-blue-500">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      يمكنك تخصيص شروط مختلفة للكتب والدورات، بالإضافة إلى الشروط العامة التي تنطبق على جميع المنتجات.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="flex items-center justify-center rounded-md bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحفظ...
                    </>
                  ) : (
                    'حفظ التغييرات'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div>
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">الإعدادات المالية</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate('/admin/dashboard/settings')}
              className="group relative p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              <HiOutlineArrowSmRight className="w-5 h-5" />
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                رجوع للإعدادات
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1 border-4 border-transparent border-t-gray-900"></div>
              </span>
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="group relative p-1 text-emerald-600 hover:text-emerald-700 dark:text-emerald-500 dark:hover:text-emerald-400"
            >
              <HiOutlineCheck className="w-5 h-5" />
              <span className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap right-1/2 transform translate-x-1/2">
                حفظ التغييرات
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 -translate-y-1 border-4 border-transparent border-t-gray-900"></div>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
          <Tab.List className="flex gap-2 rounded-xl bg-gray-100 dark:bg-gray-700 p-1 mb-6">
            {tabs.map((tab, idx) => (
              <Tab
                key={idx}
                disabled={tab.disabled}
                className={({ selected }) =>
                  classNames(
                    'w-full rounded-lg py-3 text-sm font-medium leading-5',
                    'focus:outline-none',
                    'flex items-center justify-center gap-2',
                    tab.disabled ? 'opacity-50 cursor-not-allowed' : '',
                    selected
                      ? 'bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-white/[0.12] dark:hover:bg-gray-500/50'
                  )
                }
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </Tab>
            ))}
          </Tab.List>
          <Tab.Panels>
            {tabs.map((tab, idx) => (
              <Tab.Panel 
                key={idx}
                className={classNames(
                  tab.disabled ? 'opacity-50 pointer-events-none' : ''
                )}
              >
                {tab.content}
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
};

export default FinancialSettings;
